import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api, { fmtInt, fmtPct } from "@/api";
import { useI18n } from "@/i18n";
import { useCurrency } from "@/currency";
import { useCountry } from "@/country";
import { useShowPrices } from "@/showPrices";
import { PageHeader, Tile, Loading } from "@/components/Bits";

export default function TurkeyMap() {
  const { t, lang } = useI18n();
  const { fmt: fmtMoney } = useCurrency();
  const { current: currentCountry, code: countryCode } = useCountry();
  const { showPrices } = useShowPrices();
  const [d, setD] = useState(null);
  const [metric, setMetric] = useState("pipeline_eur");

  useEffect(() => {
    api.get("/map").then((r) => setD(r.data));
  }, []);

  // If prices are hidden and current metric is the € one, fall back to units.
  const effectiveMetric = !showPrices && metric === "pipeline_eur" ? "total_units" : metric;

  if (!d) return <Loading />;

  const valued = d.cities.filter((c) => c.lat && c.lng);
  const maxVal = Math.max(...valued.map((c) => c[effectiveMetric] || 0), 1);
  const center = currentCountry.map_center || [39.0, 35.0];
  const zoom = currentCountry.map_zoom || 6;

  return (
    <div data-testid="map-page">
      <PageHeader
        title={`${currentCountry.flag || "🗺️"} ${currentCountry.name_en || countryCode}`}
        subtitle={showPrices ? t("map.subtitle") : (lang === "fr" ? "Quantité d'équipements par ville" : "Equipment quantity by city")}
        right={
          <select
            value={effectiveMetric}
            onChange={(e) => setMetric(e.target.value)}
            className="input-tech w-auto"
            data-testid="map-metric"
          >
            {showPrices && <option value="pipeline_eur">Pipeline Active (€)</option>}
            <option value="total_units">Total units</option>
            <option value="wabtec_units">Wabtec units</option>
          </select>
        }
      />
      <div className="p-4 md:p-8 space-y-4 md:space-y-5">
        <Tile testId="map-tile">
          {valued.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-[32px] mb-3">🗺️</div>
              <div className="text-[15px] font-semibold mb-1">{t("country.empty.title")}</div>
              <div className="text-[13px]" style={{ color: "var(--text-secondary)" }}>{t("country.empty.hint")}</div>
            </div>
          ) : (
          <MapContainer key={`${countryCode}-${center[0]}-${center[1]}-${zoom}`} center={center} zoom={zoom} scrollWheelZoom={true} style={{ height: 600, width: "100%" }}>
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {valued.map((c) => {
              const v = c[effectiveMetric] || 0;
              const radius = 6 + (v / maxVal) * 32;
              return (
                <CircleMarker
                  key={c.city}
                  center={[c.lat, c.lng]}
                  radius={radius}
                  pathOptions={{ color: "#ED2027", fillColor: "#ED2027", fillOpacity: 0.6, weight: 2 }}
                >
                  <Popup>
                    <div style={{ fontFamily: "IBM Plex Sans", minWidth: 200 }}>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>{c.city}</div>
                      <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, lineHeight: 1.7 }}>
                        Total units: <b>{fmtInt(c.total_units)}</b><br />
                        Wabtec: <b style={{ color: "#ED2027" }}>{fmtInt(c.wabtec_units)}</b> ({fmtPct((c.wabtec_units / Math.max(1, c.total_units)) * 100)})<br />
                        {showPrices && <>Pipeline: <b>{fmtMoney(c.pipeline_eur)}</b><br /></>}
                        Platforms: {c.platforms_count}<br />
                        Operators: {(c.operators || []).join(", ")}
                      </div>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
          </MapContainer>
          )}
        </Tile>

        {valued.length > 0 && (
        <Tile>
          <div className="overflow-x-auto">
          <table className="tech" data-testid="map-table">
            <thead>
              <tr>
                <th>{t("table.city")}</th>
                <th className="text-right">Total units</th>
                <th className="text-right">Wabtec</th>
                <th className="text-right">Share</th>
                {showPrices && <th className="text-right">Pipeline (Active €)</th>}
                <th>Operators</th>
              </tr>
            </thead>
            <tbody>
              {d.cities.sort((a, b) => (b.pipeline_eur || 0) - (a.pipeline_eur || 0)).map((c) => (
                <tr key={c.city}>
                  <td>{c.city}</td>
                  <td className="num">{fmtInt(c.total_units)}</td>
                  <td className="num text-wabtec-red">{fmtInt(c.wabtec_units)}</td>
                  <td className="num">{fmtPct((c.wabtec_units / Math.max(1, c.total_units)) * 100)}</td>
                  {showPrices && <td className="num">{fmtMoney(c.pipeline_eur)}</td>}
                  <td className="text-xs text-zinc-400">{(c.operators || []).join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Tile>
        )}
      </div>
    </div>
  );
}
